/**
 * Loading Cloudflare Turnstile, once, on demand.
 *
 * The API checks Turnstile before it will spend an SES send, so this is
 * load-bearing: without a token there is no sign-in code, and with a STALE token
 * there is no sign-in code either. A Turnstile token is single use and short
 * lived, which is the single most common way this integration breaks — a retry
 * after a failed submit reuses the spent token and fails again, forever. The
 * widget component resets after every settled request for exactly that reason.
 *
 * The script is fetched in explicit-render mode with an `onload` callback, so
 * nothing renders itself into the page and the panel owns every widget's
 * lifetime. The promise is memoised at module scope, which is what keeps React's
 * strict-mode double effect from inserting two script tags.
 */

export interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: (code?: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'compact' | 'flexible';
  action?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  retry?: 'auto' | 'never';
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Cloudflare calls this global once the script is ready; it is not a module import. */
export const TURNSTILE_READY_CALLBACK = '__pbtvTurnstileReady';
export const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile';
export const TURNSTILE_SCRIPT_BASE = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/** How long to wait for Cloudflare before telling the user it did not load. */
export const TURNSTILE_LOAD_TIMEOUT_MS = 15_000;

export function turnstileScriptSrc(): string {
  return `${TURNSTILE_SCRIPT_BASE}?render=explicit&onload=${TURNSTILE_READY_CALLBACK}`;
}

/**
 * The configured site key, or null when the build has none. Site keys are public
 * by design — the widget is useless without the matching secret, which only the
 * Lambda holds — so an empty value is a deployment mistake to report, not a
 * secret to hide.
 */
export function normaliseSiteKey(raw: string | undefined): string | null {
  const trimmed = raw?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

export function configuredSiteKey(): string | null {
  return normaliseSiteKey(import.meta.env.VITE_TURNSTILE_SITE_KEY);
}

export class TurnstileLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TurnstileLoadError';
  }
}

let loading: Promise<TurnstileApi> | null = null;

export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile !== undefined) return Promise.resolve(window.turnstile);
  if (loading !== null) return loading;

  loading = new Promise<TurnstileApi>((resolve, reject) => {
    let settled = false;

    const fail = (message: string): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      // Dropped so a later attempt — the retry button — starts clean rather than
      // resolving against the failure that is already recorded here.
      loading = null;
      reject(new TurnstileLoadError(message));
    };

    const succeed = (): void => {
      if (settled) return;
      const api = window.turnstile;
      if (api === undefined) {
        fail('Turnstile loaded but did not register itself.');
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      resolve(api);
    };

    const timer = window.setTimeout(
      () => fail('Cloudflare Turnstile did not load in time.'),
      TURNSTILE_LOAD_TIMEOUT_MS,
    );

    (window as unknown as Record<string, () => void>)[TURNSTILE_READY_CALLBACK] = succeed;

    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing !== null) {
      // A previous mount already inserted it; its onload will call back into the
      // callback we just replaced, and if it has already fired the API is there.
      if (window.turnstile !== undefined) succeed();
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = turnstileScriptSrc();
    script.async = true;
    script.defer = true;
    script.addEventListener('error', () => {
      script.remove();
      fail('Cloudflare Turnstile could not be reached.');
    });
    document.head.append(script);
  });

  return loading;
}
